import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import SampleReport from "@/pages/SampleReport";
import FeedbackVideo from "@/pages/FeedbackVideo";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Assessment from "@/pages/Assessment";
import Report from "@/pages/Report";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminQuestions from "@/pages/AdminQuestions";
import AdminResults from "@/pages/AdminResults";
import AdminStudents from "@/pages/AdminStudents";
import AdminFreeAccess from "@/pages/AdminFreeAccess";
import AdminFeedbackVideos from "@/pages/AdminFeedbackVideos";
import AdminPartners from "@/pages/AdminPartners";
import PartnerApply from "@/pages/PartnerApply";
import PartnerDashboard from "@/pages/PartnerDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import SchoolDashboard from "@/pages/SchoolDashboard";
import SchoolStudents from "@/pages/SchoolStudents";
import Vocational from "@/pages/Vocational";
import BulkOnboard from "@/pages/BulkOnboard";
import CareerExplorer from "@/pages/CareerExplorer";
import CareerExplorerRoute from "@/pages/CareerExplorerRoute";
import ClassReport from "@/pages/ClassReport";
import Certificate from "@/pages/Certificate";
import Wishlist from "@/pages/Wishlist";
import SchoolWishlists from "@/pages/SchoolWishlists";
import UniversityDashboard from "@/pages/UniversityDashboard";
import UniversitySchools from "@/pages/UniversitySchools";
import UniversityStudents from "@/pages/UniversityStudents";
import UniversityBranding from "@/pages/UniversityBranding";
import InviteAccept from "@/pages/InviteAccept";
import CareerCompare from "@/pages/CareerCompare";
import EditProfile from "@/pages/EditProfile";
import Pay from "@/pages/Pay";
import PaymentCallback from "@/pages/PaymentCallback";
import ContactUs from "@/pages/ContactUs";
import RefundPolicy from "@/pages/RefundPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/sample-report" element={<SampleReport />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/invite/:code" element={<InviteAccept />} />
              <Route path="/profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/partner/apply" element={<PartnerApply />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/feedback-video" element={<ProtectedRoute><FeedbackVideo /></ProtectedRoute>} />

              {/* Student */}
              <Route path="/dashboard" element={<ProtectedRoute roles={["student"]}><Dashboard /></ProtectedRoute>} />
              <Route path="/pay" element={<ProtectedRoute roles={["student"]}><Pay /></ProtectedRoute>} />
              <Route path="/payment/callback" element={<ProtectedRoute roles={["student"]}><PaymentCallback /></ProtectedRoute>} />
              <Route path="/assessment" element={<ProtectedRoute roles={["student"]}><Assessment /></ProtectedRoute>} />
              <Route path="/report/:id" element={<ProtectedRoute><Report /></ProtectedRoute>} />
              <Route path="/vocational" element={<ProtectedRoute roles={["student"]}><Vocational /></ProtectedRoute>} />
              <Route path="/career/:title" element={<ProtectedRoute><CareerExplorerRoute /></ProtectedRoute>} />
              <Route path="/wishlist" element={<ProtectedRoute roles={["student"]}><Wishlist /></ProtectedRoute>} />
              <Route path="/compare" element={<ProtectedRoute roles={["student"]}><CareerCompare /></ProtectedRoute>} />
              <Route path="/compare/:a/:b" element={<ProtectedRoute roles={["student"]}><CareerCompare /></ProtectedRoute>} />
              <Route path="/certificate/:id" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />

              {/* Parent */}
              <Route path="/parent" element={<ProtectedRoute roles={["parent"]}><ParentDashboard /></ProtectedRoute>} />

              {/* Counselor */}
              <Route path="/counselor" element={<ProtectedRoute roles={["counselor"]}><SchoolDashboard variant="counselor" /></ProtectedRoute>} />
              <Route path="/counselor/students" element={<ProtectedRoute roles={["counselor"]}><SchoolStudents variant="counselor" /></ProtectedRoute>} />
              <Route path="/counselor/bulk" element={<ProtectedRoute roles={["counselor"]}><BulkOnboard /></ProtectedRoute>} />
              <Route path="/counselor/class-report" element={<ProtectedRoute roles={["counselor"]}><ClassReport /></ProtectedRoute>} />
              <Route path="/counselor/wishlists" element={<ProtectedRoute roles={["counselor"]}><SchoolWishlists /></ProtectedRoute>} />

              {/* Principal */}
              <Route path="/principal" element={<ProtectedRoute roles={["principal"]}><SchoolDashboard variant="principal" /></ProtectedRoute>} />
              <Route path="/principal/students" element={<ProtectedRoute roles={["principal"]}><SchoolStudents variant="principal" /></ProtectedRoute>} />
              <Route path="/principal/bulk" element={<ProtectedRoute roles={["principal"]}><BulkOnboard /></ProtectedRoute>} />
              <Route path="/principal/class-report" element={<ProtectedRoute roles={["principal"]}><ClassReport /></ProtectedRoute>} />
              <Route path="/principal/wishlists" element={<ProtectedRoute roles={["principal"]}><SchoolWishlists /></ProtectedRoute>} />

              {/* Partner */}
              <Route path="/partner" element={<ProtectedRoute roles={["partner"]}><PartnerDashboard /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/questions" element={<ProtectedRoute roles={["admin"]}><AdminQuestions /></ProtectedRoute>} />
              <Route path="/admin/results" element={<ProtectedRoute roles={["admin"]}><AdminResults /></ProtectedRoute>} />
              <Route path="/admin/students" element={<ProtectedRoute roles={["admin"]}><AdminStudents /></ProtectedRoute>} />
              <Route path="/admin/free-access" element={<ProtectedRoute roles={["admin"]}><AdminFreeAccess /></ProtectedRoute>} />
              <Route path="/admin/feedback-videos" element={<ProtectedRoute roles={["admin"]}><AdminFeedbackVideos /></ProtectedRoute>} />
              <Route path="/admin/partners" element={<ProtectedRoute roles={["admin"]}><AdminPartners /></ProtectedRoute>} />

              {/* University (super) */}
              <Route path="/university" element={<ProtectedRoute roles={["university"]}><UniversityDashboard /></ProtectedRoute>} />
              <Route path="/university/schools" element={<ProtectedRoute roles={["university"]}><UniversitySchools /></ProtectedRoute>} />
              <Route path="/university/students" element={<ProtectedRoute roles={["university"]}><UniversityStudents /></ProtectedRoute>} />
              <Route path="/university/branding" element={<ProtectedRoute roles={["university"]}><UniversityBranding /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
